import React from "react";
import { Checkbox, Typography, Button, TextField, Collapse } from "@mui/material";
import { COLORS } from "../../utils/colors";

const LoginTabContent = ({
  suffix,
  tabType,
  // Data Collection states
  requireName,
  setRequireName,
  requireEmail,
  setRequireEmail,
  requireAge,
  setRequireAge,
  requirePhone,
  setRequirePhone,
  requireAddress,
  setRequireAddress,
  // Age restrictions
  mustBe18AndOlder,
  setMustBe18AndOlder,
  mustBe21AndOlder,
  setMustBe21AndOlder,
  // Control whether to show Terms of Use section
  hideTermsOfUse = false,
  // Settings object to update
  settings,
  // Consent props
  nameConsentEnabled,
  setNameConsentEnabled,
  nameConsentText,
  setNameConsentText,
  emailConsentEnabled,
  setEmailConsentEnabled,
  emailConsentText,
  setEmailConsentText,
  ageConsentEnabled,
  setAgeConsentEnabled,
  ageConsentText,
  setAgeConsentText,
}) => {
  // Remove local consent states since we're now using props
  // const [nameConsentEnabled, setNameConsentEnabled] = useState(
  //   settings?.[`nameConsentEnabled${suffix}`] || false
  // );
  // const [nameConsentText, setNameConsentText] = useState(
  //   settings?.[`nameConsentText${suffix}`] || ""
  // );
  // const [emailConsentEnabled, setEmailConsentEnabled] = useState(
  //   settings?.[`emailConsentEnabled${suffix}`] || false
  // );
  // const [emailConsentText, setEmailConsentText] = useState(
  //   settings?.[`emailConsentText${suffix}`] || ""
  // );
  // const [ageConsentEnabled, setAgeConsentEnabled] = useState(
  //   settings?.[`ageConsentEnabled${suffix}`] || false
  // );
  // const [ageConsentText, setAgeConsentText] = useState(
  //   settings?.[`ageConsentText${suffix}`] || ""
  // );
  const getTabTitle = () => {
    switch (tabType) {
      case "social-submissions":
        return "Social Submissions";
      case "live-user-camera":
        return "Live User Camera";
      case "polling-gaming":
        return "Polling/Gaming";
      case "light-show":
        return "Light Show";
      default:
        return "Settings";
    }
  };

  // Helper function to render consent section for each metric
  const renderConsentSection = (metricName, enabled, consentEnabled, setConsentEnabled, consentText, setConsentText) => {
    if (!enabled) return null;

    const metricKey = metricName.toLowerCase();
    const consentEnabledKey = `${metricKey}ConsentEnabled${suffix}`;
    const consentTextKey = `${metricKey}ConsentText${suffix}`;

    return (
      <div style={{ marginLeft: "32px", marginTop: "12px" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            const newValue = !consentEnabled;
            setConsentEnabled(newValue);
            settings[consentEnabledKey] = newValue;
            if (!newValue) {
              // Clear consent text when disabling
              setConsentText("");
              settings[consentTextKey] = "";
            }
          }}
          style={{
            color: consentEnabled ? COLORS.textHeader : COLORS.primary,
            backgroundColor: consentEnabled ? COLORS.primary : "transparent",
            borderColor: COLORS.primary,
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
          }}
        >
          {consentEnabled ? "Remove Consent Box" : "Add Consent Box"}
        </Button>

        <Collapse in={consentEnabled}>
          <div style={{ marginTop: "12px" }}>
            <Typography
              style={{
                color: COLORS.textLabel,
                fontSize: 12,
                marginBottom: "8px",
              }}
            >
              Consent text for {metricName}:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={consentText}
              onChange={(e) => {
                setConsentText(e.target.value);
                settings[consentTextKey] = e.target.value;
              }}
              placeholder={
                metricName === "Email" 
                  ? "*By checking this box and clicking \"Continue\", I consent to receiving informational, promotional, and marketing communications from the <company-name> and its affiliates, and I authorize them to send me emails at the email address provided above. Emails will include an unsubscribe link if you want to stop receiving emails."
                  : `Enter consent text for ${metricName.toLowerCase()} collection...`
              }
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: COLORS.popupBackground,
                  color: COLORS.textLabel,
                  fontSize: "12px",
                  "& fieldset": {
                    borderColor: COLORS.primary,
                  },
                  "&:hover fieldset": {
                    borderColor: COLORS.primary,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: COLORS.primary,
                  },
                },
              }}
            />
          </div>
        </Collapse>
      </div>
    );
  };

  return (
    <div className="loginSettingsTabPanel">
      <Typography
        variant="h6"
        style={{ color: COLORS.textHeader, marginBottom: "16px" }}
      >
        {getTabTitle()} Data Collection:
      </Typography>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <div style={{ width: "100%" }}>
          {/* Name Section */}
          <div
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => {
              const newNameValue = !requireName;
              setRequireName(newNameValue);
              settings[
                suffix
                  ? `requireName${suffix}`
                  : `requireName${
                      tabType.charAt(0).toUpperCase() +
                      tabType
                        .slice(1)
                        .replace(/-([a-z])/g, (g) => g[1].toUpperCase())
                    }`
              ] = newNameValue;

              // Clear name consent when name is disabled
              if (!newNameValue && setNameConsentEnabled) {
                setNameConsentEnabled(false);
                setNameConsentText("");
                settings[`nameConsentEnabled${suffix}`] = false;
                settings[`nameConsentText${suffix}`] = "";
              }
            }}
          >
            <Checkbox
              checked={requireName}
              sx={{
                color: COLORS.primary,
                "&.Mui-checked": { color: COLORS.primary },
              }}
            />
            <Typography style={{ color: COLORS.textLabel, fontSize: 14 }}>
              Name
            </Typography>
          </div>
          {renderConsentSection("Name", requireName, nameConsentEnabled, setNameConsentEnabled, nameConsentText, setNameConsentText)}

          {/* Email Section */}
          <div
            style={{ display: "flex", alignItems: "center", cursor: "pointer", marginTop: "16px" }}
            onClick={() => {
              const newEmailValue = !requireEmail;
              setRequireEmail(newEmailValue);
              settings[
                suffix
                  ? `requireEmail${suffix}`
                  : `requireEmail${
                      tabType.charAt(0).toUpperCase() +
                      tabType.slice(1).replace(/-([a-z])/g, (g) => g[1].toUpperCase())
                    }`
              ] = newEmailValue;

              // Clear email consent when email is disabled
              if (!newEmailValue && setEmailConsentEnabled) {
                setEmailConsentEnabled(false);
                setEmailConsentText("");
                settings[`emailConsentEnabled${suffix}`] = false;
                settings[`emailConsentText${suffix}`] = "";
              }
            }}
          >
            <Checkbox
              checked={requireEmail}
              sx={{
                color: COLORS.primary,
                "&.Mui-checked": { color: COLORS.primary },
              }}
            />
            <Typography style={{ color: COLORS.textLabel, fontSize: 14 }}>
              Email
            </Typography>
          </div>
          {renderConsentSection("Email", requireEmail, emailConsentEnabled, setEmailConsentEnabled, emailConsentText, setEmailConsentText)}

          {/* Age Section */}
          <div
            style={{ display: "flex", alignItems: "center", cursor: "pointer", marginTop: "16px" }}
            onClick={() => {
              const newAgeValue = !requireAge;
              setRequireAge(newAgeValue);
              settings[
                suffix
                  ? `requireAge${suffix}`
                  : `requireAge${
                      tabType.charAt(0).toUpperCase() +
                      tabType.slice(1).replace(/-([a-z])/g, (g) => g[1].toUpperCase())
                    }`
              ] = newAgeValue;

              // If unchecking age, also uncheck age restrictions and clear age consent
              if (!newAgeValue) {
                setMustBe18AndOlder(false);
                setMustBe21AndOlder(false);
                
                // Clear age consent when age is disabled
                if (setAgeConsentEnabled) {
                  setAgeConsentEnabled(false);
                  setAgeConsentText("");
                  settings[`ageConsentEnabled${suffix}`] = false;
                  settings[`ageConsentText${suffix}`] = "";
                }

                settings[
                  suffix
                    ? `mustBe18AndOlder${suffix}`
                    : `mustBe18AndOlder${
                        tabType.charAt(0).toUpperCase() +
                        tabType
                          .slice(1)
                          .replace(/-([a-z])/g, (g) => g[1].toUpperCase())
                      }`
                ] = false;
                settings[
                  suffix
                    ? `mustBe21AndOlder${suffix}`
                    : `mustBe21AndOlder${
                        tabType.charAt(0).toUpperCase() +
                        tabType
                          .slice(1)
                          .replace(/-([a-z])/g, (g) => g[1].toUpperCase())
                      }`
                ] = false;
              }
            }}
          >
            <Checkbox
              checked={requireAge}
              sx={{
                color: COLORS.primary,
                "&.Mui-checked": { color: COLORS.primary },
              }}
            />
            <Typography style={{ color: COLORS.textLabel, fontSize: 14 }}>
              Age
            </Typography>
          </div>
          {renderConsentSection("Age", requireAge, ageConsentEnabled, setAgeConsentEnabled, ageConsentText, setAgeConsentText)}
        </div>
      </div>

      {/* Only show Age Restrictions section if requireAge is checked */}
      {requireAge && (
        <>
          <Typography
            variant="h6"
            style={{ color: COLORS.textHeader, marginBottom: "16px" }}
          >
            Age Restrictions for {getTabTitle()}:
          </Typography>

          <div style={{ display: "flex", gap: "32px", marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => {
                // If clicking 18+, uncheck 21+ and set 18+
                if (!mustBe18AndOlder) {
                  setMustBe21AndOlder(false);
                  settings[
                    suffix
                      ? `mustBe21AndOlder${suffix}`
                      : `mustBe21AndOlder${
                          tabType.charAt(0).toUpperCase() +
                          tabType
                            .slice(1)
                            .replace(/-([a-z])/g, (g) => g[1].toUpperCase())
                        }`
                  ] = false;
                }
                setMustBe18AndOlder(!mustBe18AndOlder);
                settings[
                  suffix
                    ? `mustBe18AndOlder${suffix}`
                    : `mustBe18AndOlder${
                        tabType.charAt(0).toUpperCase() +
                        tabType
                          .slice(1)
                          .replace(/-([a-z])/g, (g) => g[1].toUpperCase())
                      }`
                ] = !mustBe18AndOlder;
              }}
            >
              <Checkbox
                checked={mustBe18AndOlder}
                sx={{
                  color: COLORS.primary,
                  "&.Mui-checked": { color: COLORS.primary },
                }}
              />
              <Typography style={{ color: COLORS.textLabel, fontSize: 14 }}>
                Must be 18 and older
              </Typography>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => {
                // If clicking 21+, uncheck 18+ and set 21+
                if (!mustBe21AndOlder) {
                  setMustBe18AndOlder(false);
                  settings[
                    suffix
                      ? `mustBe18AndOlder${suffix}`
                      : `mustBe18AndOlder${
                          tabType.charAt(0).toUpperCase() +
                          tabType
                            .slice(1)
                            .replace(/-([a-z])/g, (g) => g[1].toUpperCase())
                        }`
                  ] = false;
                }
                setMustBe21AndOlder(!mustBe21AndOlder);
                settings[
                  suffix
                    ? `mustBe21AndOlder${suffix}`
                    : `mustBe21AndOlder${
                        tabType.charAt(0).toUpperCase() +
                        tabType
                          .slice(1)
                          .replace(/-([a-z])/g, (g) => g[1].toUpperCase())
                      }`
                ] = !mustBe21AndOlder;
              }}
            >
              <Checkbox
                checked={mustBe21AndOlder}
                sx={{
                  color: COLORS.primary,
                  "&.Mui-checked": { color: COLORS.primary },
                }}
              />
              <Typography style={{ color: COLORS.textLabel, fontSize: 14 }}>
                Must be 21 and older
              </Typography>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default LoginTabContent;
